import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Iconify from "../../../../layouts/components/iconify/iconify";
import Image from "../../../../components/image/Image";
import Buttons from "../../../../components/ui/button/Buttons";
import SidebarModel from "../../../../components/ui/sidebarModel/SidebarModel";
import CreateTask from "../../../task/CreateTask";
import DropdownSelect from "../../../../components/ui/dropdown/DropdownSelect";
import Tooltip from "../../../../components/ui/tooltip/Tooltip";
import { AppIcons } from "../../../../data/appIcons";
import { encryptUrlData } from "../../../../services/CryptoService";
import formatActualHours from "../../../../lib/formatActualHours";
import { useLazyGetAllPriorityListQuery } from "../../../../app/services/priorityAPI";
import {
  MY_WORK_TABS,
  PAGE_SIZE,
  filterTasks,
  flattenMyWorkLists,
  formatDueDate,
  getVisiblePages,
  priorityOptionsFromMaster,
  projectOptionsFromAssigned,
  uniqueOptionsById,
  uniqueOptionsByLabel,
} from "./myWork.utils";

const emptyFilters = {
  search: "",
  project: "",
  priority: "",
  status: "",
};

const getPriorityMeta = (priorityName) => {
  const name = String(priorityName || "").trim().toLowerCase();
  if (name === "high") return { icon: AppIcons.High, label: "High Priority" };
  if (name === "medium") return { icon: AppIcons.Medium, label: "Medium Priority" };
  if (name === "low") return { icon: AppIcons.Low, label: "Low Priority" };
  if (!priorityName) return null;
  return { icon: null, label: `${priorityName} Priority` };
};

const PortaledTooltip = ({ label, targetId }) => {
  if (!label || !targetId) return null;

  return createPortal(
    <Tooltip
      label={label}
      targetId={targetId}
      position="top"
      positionStrategy="fixed"
      className="my-work-tooltip"
    />,
    document.body
  );
};

const TruncatedTitle = ({ id, text }) => {
  const ref = useRef(null);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !text) {
      setShowTip(false);
      return undefined;
    }

    const checkOverflow = () => {
      setShowTip(el.scrollWidth > el.clientWidth + 1);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [text]);

  return (
    <>
      <span id={id} ref={ref} className="my-work-title-text">
        {text || "—"}
      </span>
      {showTip ? <PortaledTooltip label={text} targetId={id} /> : null}
    </>
  );
};

const MyWork = ({
  activeTab,
  onTabChange,
  listsByTab,
  countsByTab,
  projects = [],
  isLoading,
  isRefreshing,
  onRefresh,
}) => {
  const [filters, setFilters] = useState(emptyFilters);
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [masterPriorities, setMasterPriorities] = useState([]);

  const [getAllPriorityList] = useLazyGetAllPriorityListQuery();

  const sourceRows = listsByTab[activeTab] || [];
  const allMyWorkRows = useMemo(() => flattenMyWorkLists(listsByTab), [listsByTab]);

  useEffect(() => {
    let cancelled = false;
    getAllPriorityList()
      .unwrap()
      .then((data) => {
        if (!cancelled) setMasterPriorities(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setMasterPriorities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [getAllPriorityList]);

  // Stable across tabs: assigned projects first; fall back to union of all My Work rows.
  const projectOptions = useMemo(() => {
    const fromAssigned = projectOptionsFromAssigned(projects);
    if (fromAssigned.length > 0) return fromAssigned;
    return uniqueOptionsById(allMyWorkRows, "projectId", "projectName");
  }, [projects, allMyWorkRows]);

  // Stable across tabs: master priority list first; fall back to union of all My Work rows.
  const priorityOptions = useMemo(() => {
    const fromMaster = priorityOptionsFromMaster(masterPriorities);
    if (fromMaster.length > 0) return fromMaster;
    return uniqueOptionsByLabel(allMyWorkRows, "priorityName");
  }, [masterPriorities, allMyWorkRows]);

  const selectedProject = useMemo(
    () => projectOptions.find((opt) => opt.value === String(filters.project)) || null,
    [projectOptions, filters.project]
  );
  const selectedPriority = useMemo(
    () => priorityOptions.find((opt) => opt.value === filters.priority) || null,
    [priorityOptions, filters.priority]
  );
  const statusOptions = useMemo(
    () => uniqueOptionsByLabel(sourceRows, "statusName"),
    [sourceRows]
  );
  const selectedStatus = useMemo(
    () => statusOptions.find((opt) => opt.value === filters.status) || null,
    [statusOptions, filters.status]
  );

  const filteredRows = useMemo(
    () => filterTasks(sourceRows, filters),
    [sourceRows, filters]
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = filteredRows.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(currentPage * PAGE_SIZE, filteredRows.length);
  const pagedRows = filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const visiblePages = getVisiblePages(currentPage, totalPages);

  useEffect(() => {
    setPage(1);
  }, [activeTab, filters]);

  useEffect(() => {
    if (activeTab !== "inProgress" && filters.status) {
      setFilters((prev) => ({ ...prev, status: "" }));
    }
  }, [activeTab, filters.status]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => setFilters(emptyFilters);

  const handleOpenTask = (task) => {
    const dataURL = {
      taskId: task.id,
      projectId: task.projectId,
    };
    window.open(`/project/EditTask/${encryptUrlData(dataURL)}`, "_blank");
  };

  const handleCreateClose = () => {
    setShowCreateModal(false);
    if (typeof onRefresh === "function") onRefresh();
  };

  const isStatusFilterDisabled = activeTab !== "inProgress";
  const hasActiveFilters = Boolean(filters.search || filters.project || filters.priority || filters.status);

  return (
    <div className="dashboard-card my-work-card">
      <div className="my-work-card__header">
        <div>
          <h3>My Work</h3>
        </div>
        <div className="my-work-card__actions">
          <Buttons
            buttonTypeClassName="refresh-btn my-work-refresh-btn"
            onClick={() => typeof onRefresh === "function" && onRefresh()}
            buttonText=""
            textWithIcon={true}
            fontIcon={true}
            iconName="ic:sharp-refresh"
            titleText="Refresh"
            isLoading={Boolean(isRefreshing)}
            isDisable={!onRefresh}
          />
          <Buttons
            textWithIcon={true}
            fontIcon={true}
            iconName="material-symbols:add-rounded"
            buttonTypeClassName="theme-btn"
            buttonText="New Task"
            onClick={() => setShowCreateModal(true)}
          />
        </div>
      </div>

      <div className="my-work-tabs" role="tablist">
        {MY_WORK_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`my-work-tabs__item ${activeTab === tab.id ? "is-active" : ""}`}
            onClick={() => onTabChange(tab.id)}
          >
            <span>{tab.label}</span>
            <em>{countsByTab[tab.id] ?? 0}</em>
          </button>
        ))}
      </div>

      <div className="my-work-filters">
        <input
          type="search"
          className="my-work-filters__search"
          placeholder="Search by title or ID…"
          value={filters.search}
          onChange={(e) => handleFilterChange("search", e.target.value)}
        />
        <div className="my-work-filters__dropdown">
          <DropdownSelect
            optionsValue={projectOptions}
            value={selectedProject}
            handleDropdownChange={(opt) => handleFilterChange("project", opt?.value || "")}
            placeholder="Project"
            isSearchable={true}
          />
        </div>
        <div className="my-work-filters__dropdown">
          <DropdownSelect
            optionsValue={priorityOptions}
            value={selectedPriority}
            handleDropdownChange={(opt) => handleFilterChange("priority", opt?.value || "")}
            placeholder="Priority"
            isSearchable={true}
          />
        </div>
        <div className="my-work-filters__dropdown">
          <DropdownSelect
            optionsValue={statusOptions}
            value={selectedStatus}
            handleDropdownChange={(opt) => handleFilterChange("status", opt?.value || "")}
            placeholder="Status"
            isSearchable={true}
            isDropdownDisabled={isStatusFilterDisabled}
          />
        </div>
        <button type="button" className="my-work-filters__reset" onClick={handleReset}>
          Reset
        </button>
      </div>

      <div className="my-work-table-wrap">
        <table className="my-work-table">
          <thead>
            <tr>
              <th>Task ID</th>
              <th>Task Title</th>
              <th>Project Name</th>
              <th>Priority</th>
              <th>Due Date</th>
              <th>Status</th>
              <th>Est.</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="my-work-empty">
                  Loading tasks…
                </td>
              </tr>
            ) : pagedRows.length > 0 ? (
              pagedRows.map((task) => {
                const due = formatDueDate(task.dueDate);
                const titleId = `mywork-title-${task.id}-${task.projectId}`;
                const priorityId = `mywork-priority-${task.id}-${task.projectId}`;
                const priorityMeta = getPriorityMeta(task.priorityName);
                const isBug = String(task.taskTypeName || "").toLowerCase() === "bug";

                return (
                  <tr key={`${task.id}-${task.projectId}`} onClick={() => handleOpenTask(task)}>
                    <td>
                      <div className="my-work-id-cell">
                        {priorityMeta ? (
                          <span
                            id={priorityId}
                            className="my-work-priority-icon"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {priorityMeta.icon ? (
                              <Image
                                imgCustomClassName="priority-icon"
                                imagePath={priorityMeta.icon}
                                altText={priorityMeta.label}
                              />
                            ) : (
                              <Iconify icon="iconoir:priority-high" width={16} />
                            )}
                            <PortaledTooltip
                              label={priorityMeta.label}
                              targetId={priorityId}
                            />
                          </span>
                        ) : null}
                        <span className={`my-work-task-id ${isBug ? "is-bug" : "is-task"}`}>
                          {task.projectTitleId || task.id}
                        </span>
                      </div>
                    </td>
                    <td>
                      <TruncatedTitle id={titleId} text={task.taskName} />
                    </td>
                    <td>
                      <span className="my-work-ellipsis">{task.projectName || "—"}</span>
                    </td>
                    <td>
                      {task.priorityName ? (
                        <span className="my-work-chip my-work-chip--priority">{task.priorityName}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <div className={`my-work-due my-work-due--${due.tone}`}>
                        <span className="my-work-due__date">{due.dateLabel}</span>
                        {due.hint ? <span className="my-work-due__hint">{due.hint}</span> : null}
                      </div>
                    </td>
                    <td>
                      {task.statusName ? (
                        <span className="my-work-chip my-work-chip--status">{task.statusName}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {task.estimateHours == null || Number.isNaN(Number(task.estimateHours))
                        ? "—"
                        : formatActualHours(task.estimateHours)}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={7} className="my-work-empty">
                  <div className="my-work-empty__content">
                    <Iconify icon="solar:checklist-minimalistic-broken" width={42} />
                    <h4>{hasActiveFilters ? "No tasks match" : "No tasks here yet"}</h4>
                    <p>
                      {hasActiveFilters
                        ? "Try adjusting or resetting filters."
                        : "Create your first task to get started."}
                    </p>
                    {!hasActiveFilters && (
                      <Buttons
                        textWithIcon={true}
                        fontIcon={true}
                        iconName="material-symbols:add-rounded"
                        buttonTypeClassName="theme-btn"
                        buttonText="New Task"
                        onClick={() => setShowCreateModal(true)}
                      />
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filteredRows.length > 0 && (
        <div className="my-work-pagination">
          <span>
            Showing {pageStart}–{pageEnd} of {filteredRows.length}
          </span>
          <div className="my-work-pagination__actions">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              Prev
            </button>
            {visiblePages.map((pageNum) => (
              <button
                key={pageNum}
                type="button"
                className={pageNum === currentPage ? "is-active" : ""}
                onClick={() => setPage(pageNum)}
              >
                {pageNum}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showCreateModal && (
        <SidebarModel
          isOpen={showCreateModal}
          onClose={handleCreateClose}
          modalTitle="Create Task"
          modalSize="w-50"
          backdropClosable={false}
        >
          <CreateTask projectId={0} typeId={1} onClose={handleCreateClose} />
        </SidebarModel>
      )}
    </div>
  );
};

export default MyWork;
